import { useState, useCallback, useEffect } from 'react';
import { Popover } from '@headlessui/react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isSameMonth, isSameDay, startOfWeek, endOfWeek } from 'date-fns';
import { enGB } from 'date-fns/locale';

export default function DateRangePicker({ onChange, initialDateRange }) {
  const [selectedRange, setSelectedRange] = useState(initialDateRange || { start: null, end: null });
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Update selected range when initialDateRange changes
  useEffect(() => {
    if (initialDateRange) {
      setSelectedRange(initialDateRange);
    }
  }, [initialDateRange]);
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { locale: enGB });
  const calendarEnd = endOfWeek(monthEnd, { locale: enGB });

  const days = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd
  });

  const handleDateClick = useCallback((date, close) => {
    if (!selectedRange.start || (selectedRange.start && selectedRange.end)) {
      // Start new selection
      setSelectedRange({ start: date, end: null });
      onChange({ start: date, end: null });
    } else {
      // Complete the range
      const newRange = date < selectedRange.start
        ? { start: date, end: selectedRange.start }
        : { start: selectedRange.start, end: date };
      
      setSelectedRange(newRange);
      onChange(newRange);
      close(); // Close the popover when range is complete
    }
  }, [selectedRange, onChange]);

  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <Popover className="relative">
      <Popover.Button className="w-full flex items-center gap-2 px-3 py-2 border rounded-md text-sm">
        <CalendarIcon className="h-5 w-5 text-gray-400" />
        <span className="text-gray-700">
          {selectedRange.start
            ? `${format(selectedRange.start, 'MMM dd, yyyy')}${
                selectedRange.end ? ` - ${format(selectedRange.end, 'MMM dd, yyyy')}` : ''
              }`
            : 'Select dates'}
        </span>
      </Popover.Button>

      <Popover.Panel>
        {({ close }) => (
          <div className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-auto">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <button
                  onClick={handlePrevMonth}
                  className="p-1 hover:bg-gray-100 rounded"
                  type="button"
                >
                  ←
                </button>
                <span className="text-sm font-medium">
                  {format(currentMonth, 'MMMM yyyy')}
                </span>
                <button
                  onClick={handleNextMonth}
                  className="p-1 hover:bg-gray-100 rounded"
                  type="button"
                >
                  →
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1">
                {dayNames.map(day => (
                  <div key={day} className="text-xs text-gray-500 text-center py-1">
                    {day}
                  </div>
                ))}
                {days.map((day) => {
                  const isSelected = selectedRange.start && (
                    isSameDay(day, selectedRange.start) ||
                    (selectedRange.end && 
                      day >= selectedRange.start && 
                      day <= selectedRange.end)
                  );
                  const isOutsideMonth = !isSameMonth(day, currentMonth);

                  return (
                    <button
                      key={day.toString()}
                      onClick={() => handleDateClick(day, close)}
                      type="button"
                      className={`
                        p-2 text-sm rounded-md
                        ${isOutsideMonth ? 'text-gray-300' : ''}
                        ${isToday(day) ? 'border border-gray-300' : ''}
                        ${isSelected ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'}
                      `}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Popover.Panel>
    </Popover>
  );
} 